// Procedurally baked sprite textures, per-kind silhouettes ported from v0.6.
// Each enemy kind gets its own bake function with a distinct polygon path.
// Player ship keeps richer 2026 treatment (chromatic edge, scan-lines, cockpit gradient).
// Enemies use clean silhouette + minimal accent + soft outer halo to keep silhouettes legible.

import { Texture } from 'pixi.js';

function makeCanvas(w: number, h: number): { c: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d')!;
  return { c, ctx };
}

function hex(n: number): string {
  return '#' + n.toString(16).padStart(6, '0');
}

function rgba(n: number, a: number): string {
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  return `rgba(${r},${g},${b},${a})`;
}

function shade(n: number, t: number): number {
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  const f = (c: number) => Math.round(t > 0 ? c + (255 - c) * t : c * (1 + t));
  return (f(r) << 16) | (f(g) << 8) | f(b);
}

// Scatter small dark rivets + panel-line scratches inside a clipped hull.
// drawClipPath sets up the clip region. Random positions, jittered sizes.
function greebles(
  ctx: CanvasRenderingContext2D,
  drawClipPath: () => void,
  bounds: { x0: number; y0: number; w: number; h: number },
  rivetCount = 14,
  lineCount = 4,
  seed = 0,
): void {
  ctx.save();
  drawClipPath();
  ctx.clip();
  // Deterministic-ish: mix Math.random with seed offset so the bake is stable per call
  const rand = (): number => Math.random();
  // Rivet dots
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  for (let i = 0; i < rivetCount; i++) {
    const x = bounds.x0 + rand() * bounds.w;
    const y = bounds.y0 + rand() * bounds.h;
    const r = 0.6 + rand() * 1.0;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Bright micro-highlights (top of rivet)
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  for (let i = 0; i < Math.floor(rivetCount * 0.6); i++) {
    const x = bounds.x0 + rand() * bounds.w;
    const y = bounds.y0 + rand() * bounds.h;
    ctx.beginPath();
    ctx.arc(x, y, 0.4, 0, Math.PI * 2);
    ctx.fill();
  }
  // Panel-line scratches
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = 0.6;
  for (let i = 0; i < lineCount; i++) {
    const ax = bounds.x0 + rand() * bounds.w;
    const ay = bounds.y0 + rand() * bounds.h;
    const len = 6 + rand() * 14;
    const ang = rand() * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(ax + Math.cos(ang) * len, ay + Math.sin(ang) * len);
    ctx.stroke();
  }
  ctx.restore();
  void seed;
}

function scanLines(ctx: CanvasRenderingContext2D, x0: number, y0: number, w: number, h: number, alpha = 0.06): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = '#ffffff';
  for (let y = y0; y < y0 + h; y += 2) ctx.fillRect(x0, y, w, 1);
  ctx.restore();
}

function chromaRing(ctx: CanvasRenderingContext2D, drawPath: () => void, baseColor: number): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.shadowBlur = 18;
  ctx.shadowColor = hex(baseColor);
  ctx.lineWidth = 4;
  ctx.strokeStyle = rgba(0x00ffff, 0.15);
  drawPath(); ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.lineWidth = 2.6;
  ctx.strokeStyle = rgba(0xff44ff, 0.35);
  drawPath(); ctx.stroke();
  ctx.lineWidth = 1.6;
  ctx.strokeStyle = rgba(0xffffff, 0.95);
  drawPath(); ctx.stroke();
  ctx.restore();
}

// Helper: trace a polygon from a flat coord list and stroke + fill.
function drawPoly(ctx: CanvasRenderingContext2D, coords: number[]): void {
  ctx.beginPath();
  for (let i = 0; i < coords.length; i += 2) {
    if (i === 0) ctx.moveTo(coords[i], coords[i + 1]);
    else ctx.lineTo(coords[i], coords[i + 1]);
  }
  ctx.closePath();
}

// Soft outer halo behind a silhouette — single radial fill matched to stroke color.
function drawHalo(ctx: CanvasRenderingContext2D, radius: number, color: number, alpha = 0.18): void {
  ctx.save();
  const grd = ctx.createRadialGradient(0, 0, radius * 0.4, 0, 0, radius);
  grd.addColorStop(0, rgba(color, alpha));
  grd.addColorStop(1, rgba(color, 0));
  ctx.fillStyle = grd;
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// ── PLAYER SHIP ─────────────────────────────────────────────────────────────
// True dart/delta-wing fighter silhouette. Tier 0 = base dart; higher tiers
// bolt on canards, weapon pylons, secondary engines, cruciform fins.
function bakePlayerShip(
  hull: number, hullEdge: number, accent: number, cockpitCore: number, cockpitMid: number,
  tier = 0,
): Texture {
  const W = 144, H = 112;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);
  ctx.imageSmoothingEnabled = true;

  // Soft outer halo
  for (let pass = 0; pass < 3; pass++) {
    const r = 44 + pass * 6;
    ctx.beginPath();
    ctx.ellipse(0, 0, r * 1.4, r * 0.7, 0, 0, Math.PI * 2);
    ctx.fillStyle = rgba(hull, 0.06 - pass * 0.015);
    ctx.fill();
  }

  // ── TIER 5: cruciform vertical fins (top + bottom) — drawn behind body
  if (tier >= 5) {
    ctx.fillStyle = hex(shade(hull, -0.4));
    ctx.beginPath();
    ctx.moveTo(-4, -36); ctx.lineTo(8, -32); ctx.lineTo(-12, -16); ctx.closePath();
    ctx.fill();
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = hex(hullEdge);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-4, 36); ctx.lineTo(8, 32); ctx.lineTo(-12, 16); ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  // ── TIER 4: forward canards behind wings
  if (tier >= 4) {
    ctx.fillStyle = hex(shade(hull, -0.3));
    ctx.beginPath();
    ctx.moveTo(20, -10); ctx.lineTo(34, -22); ctx.lineTo(38, -16); ctx.lineTo(28, -8); ctx.closePath();
    ctx.fill();
    ctx.lineWidth = 1.2;
    ctx.strokeStyle = hex(hullEdge);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(20, 10); ctx.lineTo(34, 22); ctx.lineTo(38, 16); ctx.lineTo(28, 8); ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  // ── TIER 3: extended wing-tip extensions (cyan markers)
  if (tier >= 3) {
    ctx.fillStyle = hex(accent);
    ctx.beginPath();
    ctx.moveTo(-12, -28); ctx.lineTo(-22, -36); ctx.lineTo(-26, -32); ctx.lineTo(-16, -26); ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(-12, 28); ctx.lineTo(-22, 36); ctx.lineTo(-26, 32); ctx.lineTo(-16, 26); ctx.closePath();
    ctx.fill();
  }

  // ── Main hull (sharp dart with swept-back delta wings)
  const hullPath = (): void => {
    ctx.beginPath();
    ctx.moveTo(56, 0);          // sharp nose
    ctx.lineTo(40, -6);
    ctx.lineTo(28, -10);
    ctx.lineTo(8, -16);         // wing root leading edge
    ctx.lineTo(-12, -28);       // wing tip
    ctx.lineTo(-26, -16);       // wing trailing edge
    ctx.lineTo(-36, -10);
    ctx.lineTo(-42, -4);
    ctx.lineTo(-42, 4);
    ctx.lineTo(-36, 10);
    ctx.lineTo(-26, 16);
    ctx.lineTo(-12, 28);
    ctx.lineTo(8, 16);
    ctx.lineTo(28, 10);
    ctx.lineTo(40, 6);
    ctx.closePath();
  };

  hullPath();
  const g1 = ctx.createLinearGradient(56, 0, -42, 0);
  g1.addColorStop(0, hex(shade(hull, 0.5)));
  g1.addColorStop(0.4, hex(hull));
  g1.addColorStop(0.85, hex(shade(hull, -0.55)));
  g1.addColorStop(1, hex(shade(hull, -0.75)));
  ctx.fillStyle = g1;
  ctx.fill();

  // Vertical 3D shading mask
  ctx.save();
  hullPath(); ctx.clip();
  const gV = ctx.createLinearGradient(0, -28, 0, 28);
  gV.addColorStop(0, rgba(0xffffff, 0.20));
  gV.addColorStop(0.5, rgba(0xffffff, 0));
  gV.addColorStop(1, rgba(0x000000, 0.34));
  ctx.fillStyle = gV;
  ctx.fillRect(-56, -32, 112, 64);
  ctx.restore();

  // Scan-line panel texture
  ctx.save();
  hullPath(); ctx.clip();
  scanLines(ctx, -56, -32, 112, 64, 0.05);
  ctx.restore();

  // Greebles — noise-driven rivets + panel scratches
  greebles(ctx, hullPath, { x0: -40, y0: -28, w: 96, h: 56 }, 18, 5);

  // Panel-break line (longitudinal centerline + wing seams)
  ctx.save();
  hullPath(); ctx.clip();
  ctx.strokeStyle = rgba(0x000000, 0.4);
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(48, 0); ctx.lineTo(-36, 0);
  ctx.moveTo(20, -14); ctx.lineTo(-10, -6);
  ctx.moveTo(20,  14); ctx.lineTo(-10,  6);
  ctx.stroke();
  ctx.restore();

  // Chromatic edge ring
  chromaRing(ctx, hullPath, hullEdge);

  // ── TIER 2: under-wing weapon pods
  if (tier >= 2) {
    ctx.fillStyle = hex(shade(hull, -0.4));
    ctx.fillRect(-2, -24, 14, 4);
    ctx.fillRect(-2, 20, 14, 4);
    ctx.fillStyle = hex(accent);
    ctx.fillRect(-3, -22, 2, 2);
    ctx.fillRect(-3, 21, 2, 2);
    ctx.lineWidth = 0.8;
    ctx.strokeStyle = hex(hullEdge);
    ctx.strokeRect(-2, -24, 14, 4);
    ctx.strokeRect(-2, 20, 14, 4);
  }

  // ── TIER 1: secondary fins along wings
  if (tier >= 1) {
    ctx.lineWidth = 1.6;
    ctx.strokeStyle = hex(accent);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(0, -18); ctx.lineTo(-18, -24);
    ctx.moveTo(0,  18); ctx.lineTo(-18,  24);
    ctx.stroke();
  }

  // Cockpit canopy (radial-gradient sphere)
  ctx.beginPath();
  ctx.ellipse(20, -2, 13, 8, -0.1, 0, Math.PI * 2);
  const gCock = ctx.createRadialGradient(17, -5, 0, 20, -2, 13);
  gCock.addColorStop(0, hex(cockpitCore));
  gCock.addColorStop(0.4, hex(cockpitMid));
  gCock.addColorStop(0.85, hex(shade(cockpitMid, -0.5)));
  gCock.addColorStop(1, hex(0x001020));
  ctx.fillStyle = gCock;
  ctx.fill();
  ctx.lineWidth = 1.0;
  ctx.strokeStyle = rgba(0xaaeeff, 0.95);
  ctx.stroke();
  // Cockpit specular
  ctx.beginPath();
  ctx.ellipse(15, -5, 4, 1.6, -0.25, 0, Math.PI * 2);
  ctx.fillStyle = rgba(0xffffff, 0.9);
  ctx.fill();

  // Engine bell ports (rear)
  const engineCount = tier >= 3 ? 4 : tier >= 1 ? 3 : 1;
  ctx.fillStyle = rgba(shade(hull, -0.7), 0.9);
  if (engineCount === 1) {
    ctx.fillRect(-42, -5, 6, 10);
  } else if (engineCount === 3) {
    ctx.fillRect(-42, -10, 6, 5);
    ctx.fillRect(-42, -2, 6, 4);
    ctx.fillRect(-42, 6, 6, 5);
  } else {
    // 4 engines (2x2)
    ctx.fillRect(-42, -14, 6, 5);
    ctx.fillRect(-42, -7, 6, 5);
    ctx.fillRect(-42, 2, 6, 5);
    ctx.fillRect(-42, 9, 6, 5);
  }
  // Hot engine cores
  ctx.fillStyle = rgba(0xffaa00, 0.95);
  if (engineCount === 1) ctx.fillRect(-39, -3, 1, 6);
  else if (engineCount === 3) {
    ctx.fillRect(-39, -8, 1, 2);
    ctx.fillRect(-39, -1, 1, 2);
    ctx.fillRect(-39, 7, 1, 2);
  } else {
    ctx.fillRect(-39, -12, 1, 2);
    ctx.fillRect(-39, -5, 1, 2);
    ctx.fillRect(-39, 4, 1, 2);
    ctx.fillRect(-39, 11, 1, 2);
  }

  // Forward nose light
  ctx.beginPath();
  ctx.arc(54, 0, 2.6, 0, Math.PI * 2);
  ctx.fillStyle = rgba(0xffffff, 0.95);
  ctx.shadowBlur = 10;
  ctx.shadowColor = hex(0xffffff);
  ctx.fill();
  ctx.shadowBlur = 0;

  // ── TIER 5: outer halo ring around entire ship
  if (tier >= 5) {
    ctx.strokeStyle = rgba(accent, 0.45);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(0, 0, 56, 0, Math.PI * 2);
    ctx.stroke();
  }

  // ── TIER 6: prism arrays on wings — three small triangular crystals each side
  if (tier >= 6) {
    for (const sy of [-26, 26]) {
      for (let i = 0; i < 3; i++) {
        const px = -8 + i * 6;
        ctx.beginPath();
        ctx.moveTo(px, sy);
        ctx.lineTo(px + 2.5, sy - (sy > 0 ? -4 : 4));
        ctx.lineTo(px + 5, sy);
        ctx.closePath();
        ctx.fillStyle = rgba(0xcc88ff, 0.9);
        ctx.fill();
        ctx.strokeStyle = rgba(0xffffff, 0.85);
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }
    }
  }

  // ── TIER 7: missile pods — 4 cylindrical tubes mounted under-wing
  if (tier >= 7) {
    for (const sy of [-24, 24]) {
      for (let i = 0; i < 2; i++) {
        const px = 6 + i * 8;
        ctx.fillStyle = hex(shade(hull, -0.5));
        ctx.fillRect(px, sy - 2, 10, 4);
        ctx.strokeStyle = hex(0xff44cc);
        ctx.lineWidth = 0.8;
        ctx.strokeRect(px, sy - 2, 10, 4);
        // Hot tip
        ctx.fillStyle = rgba(0xff66ff, 0.95);
        ctx.fillRect(px + 9, sy - 1, 2, 2);
      }
    }
  }

  // ── TIER 8: drone bays — small docked drone shapes on each wing
  if (tier >= 8) {
    for (const sy of [-30, 30]) {
      ctx.beginPath();
      ctx.moveTo(-6, sy);
      ctx.lineTo(-2, sy - 3);
      ctx.lineTo(2, sy);
      ctx.lineTo(-2, sy + 3);
      ctx.closePath();
      ctx.fillStyle = rgba(0x88ffaa, 0.85);
      ctx.fill();
      ctx.strokeStyle = rgba(0xffffff, 0.9);
      ctx.lineWidth = 0.7;
      ctx.stroke();
      // Drone eye
      ctx.beginPath();
      ctx.arc(-2, sy, 1, 0, Math.PI * 2);
      ctx.fillStyle = rgba(0xffffff, 1);
      ctx.fill();
    }
  }

  // ── TIER 9: chain cannon barrels — heavy linked barrels along the centerline
  if (tier >= 9) {
    ctx.fillStyle = hex(shade(hull, -0.6));
    for (let i = 0; i < 3; i++) {
      const px = 30 - i * 10;
      ctx.fillRect(px, -2, 10, 4);
      ctx.strokeStyle = rgba(0xffcc44, 0.9);
      ctx.lineWidth = 0.8;
      ctx.strokeRect(px, -2, 10, 4);
    }
    // Barrel tips
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.arc(30 - i * 10 + 11, 0, 1.6, 0, Math.PI * 2);
      ctx.fillStyle = rgba(0xffcc44, 0.95);
      ctx.fill();
    }
  }

  // ── TIER 10: fractal halo — three concentric rings of decreasing alpha
  if (tier >= 10) {
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.arc(0, 0, 60 + i * 7, 0, Math.PI * 2);
      ctx.strokeStyle = rgba(0xffffff, 0.55 - i * 0.15);
      ctx.lineWidth = 1.5 - i * 0.3;
      ctx.stroke();
    }
    // 8 orbital accent dots on outer ring
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(Math.cos(a) * 74, Math.sin(a) * 74, 1.4, 0, Math.PI * 2);
      ctx.fillStyle = rgba(0xffffff, 0.95);
      ctx.fill();
    }
  }

  return Texture.from(c);
}

function bakePlayerShipTiers(hull: number, hullEdge: number, accent: number, c0: number, c1: number): Texture[] {
  // 11 tiers (0..10) — each adds visible weapon hardware: fins / pods / canards / engines / halo
  // / prisms / missiles / drone bays / chain cannons / fractal halo.
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((t) => bakePlayerShip(hull, hullEdge, accent, c0, c1, t));
}

// ── PLAYER 2 (v0.6 cyan-tip silhouette) ─────────────────────────────────────
function bakePlayer2(): Texture {
  const W = 96, H = 64;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);

  drawHalo(ctx, 36, 0x00eeff, 0.15);

  // Hull (v0.6 P2 path)
  const hullPath = (): void => {
    drawPoly(ctx, [22, 0, 2, -14, -8, -10, -20, -15, -22, 0, -20, 15, -8, 10, 2, 14]);
  };

  hullPath();
  const gFill = ctx.createLinearGradient(22, 0, -22, 0);
  gFill.addColorStop(0, hex(shade(0x00bbcc, 0.3)));
  gFill.addColorStop(0.7, hex(0x00bbcc));
  gFill.addColorStop(1, hex(shade(0x00bbcc, -0.5)));
  ctx.fillStyle = gFill;
  ctx.fill();
  ctx.shadowBlur = 14;
  ctx.shadowColor = hex(0x00eeff);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = hex(0x55eeff);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Engine glow halo (cyan radial)
  const gEng = ctx.createRadialGradient(-22, 0, 0, -22, 0, 18);
  gEng.addColorStop(0, rgba(0x00ccff, 0.9));
  gEng.addColorStop(1, rgba(0x0064c8, 0));
  ctx.fillStyle = gEng;
  ctx.beginPath();
  ctx.arc(-22, 0, 18, 0, Math.PI * 2);
  ctx.fill();

  // Cockpit dot
  ctx.beginPath();
  ctx.arc(8, 0, 4, 0, Math.PI * 2);
  const gCock = ctx.createRadialGradient(7, -1, 0, 8, 0, 4);
  gCock.addColorStop(0, hex(0xeeffff));
  gCock.addColorStop(1, hex(0x003344));
  ctx.fillStyle = gCock;
  ctx.fill();
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = hex(0x00ccdd);
  ctx.stroke();

  return Texture.from(c);
}

// ── ENEMIES — KIND-SPECIFIC SILHOUETTES (v0.6 verbatim) ────────────────────

function bakeDrone(): Texture {
  const W = 48, H = 48;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);
  drawHalo(ctx, 20, 0xff44cc, 0.30);
  drawPoly(ctx, [12, 0, -6, -8, -10, 0, -6, 8]);
  ctx.fillStyle = hex(0x220011);
  ctx.fill();
  ctx.shadowBlur = 18;
  ctx.shadowColor = hex(0xff44cc);
  ctx.lineWidth = 1.6;
  ctx.strokeStyle = hex(0xff44cc);
  ctx.stroke();
  ctx.shadowBlur = 12;
  ctx.beginPath();
  ctx.arc(0, 0, 4, 0, Math.PI * 2);
  ctx.fillStyle = hex(0xff88ee);
  ctx.fill();
  ctx.shadowBlur = 0;
  return Texture.from(c);
}

function bakeScout(): Texture {
  const W = 56, H = 40;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);
  drawHalo(ctx, 26, 0xff0000, 0.28);
  drawPoly(ctx, [-20, 0, 2, -13, 14, -7, 8, 0, 14, 7, 2, 13]);
  ctx.fillStyle = hex(0x220000);
  ctx.fill();
  ctx.shadowBlur = 16;
  ctx.shadowColor = hex(0xff0000);
  ctx.lineWidth = 1.8;
  ctx.strokeStyle = hex(0xff0000);
  ctx.stroke();
  ctx.shadowBlur = 0;
  // Engine core
  ctx.shadowBlur = 10; ctx.shadowColor = hex(0xff6666);
  ctx.beginPath();
  ctx.arc(-8, 0, 5.5, 0, Math.PI * 2);
  ctx.fillStyle = hex(0xff9999);
  ctx.fill();
  ctx.shadowBlur = 0;
  return Texture.from(c);
}

function bakeFighter(): Texture {
  const W = 72, H = 56;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);
  drawHalo(ctx, 34, 0xff6600, 0.28);
  drawPoly(ctx, [-26, 0, -8, -18, 18, -12, 24, -4, 24, 4, 18, 12, -8, 18]);
  ctx.fillStyle = hex(0x1a0a00);
  ctx.fill();
  ctx.shadowBlur = 18;
  ctx.shadowColor = hex(0xff6600);
  ctx.lineWidth = 1.8;
  ctx.strokeStyle = hex(0xff6600);
  ctx.stroke();
  // Wing gun rails — two horizontal lines matching HTML original
  ctx.shadowBlur = 10;
  ctx.lineWidth = 3;
  ctx.strokeStyle = hex(0xff6600);
  ctx.beginPath(); ctx.moveTo(-6, -20); ctx.lineTo(-28, -20); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-6, 20);  ctx.lineTo(-28, 20);  ctx.stroke();
  ctx.shadowBlur = 0;
  // Center core glow
  ctx.shadowBlur = 12; ctx.shadowColor = hex(0xff8800);
  ctx.beginPath();
  ctx.arc(-4, 0, 6, 0, Math.PI * 2);
  ctx.fillStyle = hex(0xff8800);
  ctx.fill();
  ctx.shadowBlur = 0;
  return Texture.from(c);
}

function bakeGunship(): Texture {
  const W = 96, H = 72;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);
  drawHalo(ctx, 42, 0xaa44ff, 0.22);
  // v0.6 cut-hex hull (8-vertex)
  const hullPath = (): void => {
    drawPoly(ctx, [-40, -10, -28, -30, 22, -30, 40, -15, 40, 15, 22, 30, -28, 30, -40, 10]);
  };
  hullPath();
  ctx.fillStyle = hex(0x0d0022);
  ctx.fill();
  ctx.shadowBlur = 18;
  ctx.shadowColor = hex(0xaa44ff);
  ctx.lineWidth = 2;
  ctx.strokeStyle = hex(0xaa44ff);
  ctx.stroke();
  greebles(ctx, hullPath, { x0: -38, y0: -28, w: 76, h: 56 }, 16, 4);
  ctx.shadowBlur = 8;
  // 4 turret circles at corners
  for (const [tx, ty] of [[-14, -30], [12, -30], [-14, 30], [12, 30]] as const) {
    ctx.beginPath();
    ctx.arc(tx, ty, 7, 0, Math.PI * 2);
    ctx.fillStyle = hex(0xcc66ff);
    ctx.fill();
  }
  ctx.shadowBlur = 0;
  // Radial-gradient core
  const gC = ctx.createRadialGradient(0, 0, 0, 0, 0, 14);
  gC.addColorStop(0, hex(0xcc88ff));
  gC.addColorStop(1, hex(0x330055));
  ctx.fillStyle = gC;
  ctx.beginPath();
  ctx.arc(0, 0, 14, 0, Math.PI * 2);
  ctx.fill();
  return Texture.from(c);
}

function bakeBomber(): Texture {
  const W = 80, H = 64;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);
  drawHalo(ctx, 32, 0xff5500, 0.22);
  // Wide ellipse
  const hullPath = (): void => {
    ctx.beginPath();
    ctx.ellipse(0, 0, 28, 20, 0, 0, Math.PI * 2);
  };
  hullPath();
  ctx.fillStyle = hex(0x1a0800);
  ctx.fill();
  ctx.shadowBlur = 16;
  ctx.shadowColor = hex(0xff5500);
  ctx.lineWidth = 2;
  ctx.strokeStyle = hex(0xff5500);
  ctx.stroke();
  ctx.shadowBlur = 0;
  greebles(ctx, hullPath, { x0: -26, y0: -18, w: 52, h: 36 }, 12, 3);
  // Crosshair lines
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = hex(0xffaa33);
  ctx.beginPath();
  ctx.moveTo(-20, 0); ctx.lineTo(20, 0);
  ctx.moveTo(0, -18); ctx.lineTo(0, 18);
  ctx.stroke();
  // Amber core dot
  ctx.shadowBlur = 10;
  ctx.shadowColor = hex(0xffaa00);
  ctx.beginPath();
  ctx.arc(0, 0, 8, 0, Math.PI * 2);
  ctx.fillStyle = hex(0xff8800);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.arc(0, 0, 3, 0, Math.PI * 2);
  ctx.fillStyle = hex(0xffcc00);
  ctx.fill();
  return Texture.from(c);
}

function bakeTank(): Texture {
  const W = 112, H = 112;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);
  drawHalo(ctx, 48, 0xcc7700, 0.22);
  // 6-vertex hex with alternating r=44/r=34
  const hullPath = (): void => {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const r = (i % 2 === 0) ? 44 : 34;
      const x = Math.cos(a) * r, y = Math.sin(a) * r;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
  };
  hullPath();
  ctx.fillStyle = hex(0x1a0e00);
  ctx.fill();
  ctx.shadowBlur = 20;
  ctx.shadowColor = hex(0xcc7700);
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = hex(0xcc7700);
  ctx.stroke();
  ctx.shadowBlur = 0;
  greebles(ctx, hullPath, { x0: -42, y0: -42, w: 84, h: 84 }, 22, 6);
  // 4 tread circles at corners
  ctx.lineWidth = 2;
  ctx.strokeStyle = hex(0xffaa00);
  for (const [tx, ty] of [[-18, -18], [18, -18], [-18, 18], [18, 18]] as const) {
    ctx.beginPath();
    ctx.arc(tx, ty, 7, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Amber core
  ctx.shadowBlur = 12;
  ctx.shadowColor = hex(0xcc7700);
  ctx.beginPath();
  ctx.arc(0, 0, 14, 0, Math.PI * 2);
  ctx.fillStyle = hex(0xcc7700);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.arc(0, 0, 5, 0, Math.PI * 2);
  ctx.fillStyle = hex(0xffffff);
  ctx.fill();
  return Texture.from(c);
}

function bakeCarrier(): Texture {
  const W = 112, H = 80;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);
  drawHalo(ctx, 44, 0x00ccff, 0.22);
  // Wide ellipse 40×22
  const hullPath = (): void => {
    ctx.beginPath();
    ctx.ellipse(0, 0, 40, 22, 0, 0, Math.PI * 2);
  };
  hullPath();
  ctx.fillStyle = hex(0x001a22);
  ctx.fill();
  ctx.shadowBlur = 16;
  ctx.shadowColor = hex(0x00ccff);
  ctx.lineWidth = 2;
  ctx.strokeStyle = hex(0x00ccff);
  ctx.stroke();
  ctx.shadowBlur = 0;
  greebles(ctx, hullPath, { x0: -38, y0: -20, w: 76, h: 40 }, 18, 5);
  // 6 docking-port lines
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = hex(0x44ffff);
  for (const [px, py] of [[-16, -18], [0, -20], [16, -18], [-16, 18], [0, 20], [16, 18]] as const) {
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px, py + (py < 0 ? -10 : 10));
    ctx.stroke();
  }
  // Cyan core
  ctx.shadowBlur = 10;
  ctx.shadowColor = hex(0x00ffff);
  ctx.beginPath();
  ctx.arc(0, 0, 10, 0, Math.PI * 2);
  ctx.fillStyle = hex(0x00ffff);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.arc(0, 0, 4, 0, Math.PI * 2);
  ctx.fillStyle = hex(0xffffff);
  ctx.fill();
  return Texture.from(c);
}

function bakeStealth(): Texture {
  const W = 48, H = 40;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);
  drawHalo(ctx, 18, 0xaa44ff, 0.18);
  drawPoly(ctx, [12, 0, -8, -10, -12, 0, -8, 10]);
  ctx.fillStyle = hex(0x110022);
  ctx.fill();
  ctx.shadowBlur = 10;
  ctx.shadowColor = hex(0xaa44ff);
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = hex(0xcc66ff);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.arc(0, 0, 4, 0, Math.PI * 2);
  ctx.fillStyle = hex(0xcc44ff);
  ctx.fill();
  return Texture.from(c);
}

function bakeInterceptor(): Texture {
  const W = 56, H = 40;
  const { c, ctx } = makeCanvas(W, H);
  ctx.translate(W / 2, H / 2);
  drawHalo(ctx, 22, 0xffdd00, 0.22);
  drawPoly(ctx, [18, 0, 4, -10, -14, -6, -18, 0, -14, 6, 4, 10]);
  ctx.fillStyle = hex(0x1a1800);
  ctx.fill();
  ctx.shadowBlur = 14;
  ctx.shadowColor = hex(0xffdd00);
  ctx.lineWidth = 1.6;
  ctx.strokeStyle = hex(0xffdd00);
  ctx.stroke();
  ctx.shadowBlur = 8;
  ctx.beginPath();
  ctx.arc(4, 0, 4, 0, Math.PI * 2);
  ctx.fillStyle = hex(0xffee44);
  ctx.fill();
  return Texture.from(c);
}

// Soft white radial-gradient particle/bullet glow.
function bakeParticleGlow(): Texture {
  const W = 64, H = 64;
  const { c, ctx } = makeCanvas(W, H);
  const grd = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  grd.addColorStop(0,    'rgba(255,255,255,1)');
  grd.addColorStop(0.18, 'rgba(255,255,255,0.95)');
  grd.addColorStop(0.45, 'rgba(255,255,255,0.45)');
  grd.addColorStop(0.85, 'rgba(255,255,255,0.05)');
  grd.addColorStop(1,    'rgba(255,255,255,0)');
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, W, H);
  return Texture.from(c);
}

// Boss + miniboss switch to runtime Graphics for animation — they don't bake here.
// Keep neutral fallback Textures so the SPRITES.enemy lookup never returns undefined
// for kinds that may briefly path through Sprite-based code.
function bakeFallbackDisc(color: number, radius: number): Texture {
  const W = Math.max(64, radius * 2 + 20);
  const { c, ctx } = makeCanvas(W, W);
  ctx.translate(W / 2, W / 2);
  drawHalo(ctx, radius * 1.2, color, 0.22);
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.fillStyle = hex(shade(color, -0.4));
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = hex(color);
  ctx.stroke();
  return Texture.from(c);
}

// White-only radial glow texture — tinted at sprite use site.
// `inner` controls how far the bright core extends (0..1, 0=tiny core).
function bakeRadialGlow(size: number, inner = 0.18): Texture {
  const W = size, H = size;
  const { c, ctx } = makeCanvas(W, H);
  const grd = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, W / 2);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(inner, 'rgba(255,255,255,0.85)');
  grd.addColorStop(0.55, 'rgba(255,255,255,0.32)');
  grd.addColorStop(0.85, 'rgba(255,255,255,0.06)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, W, H);
  return Texture.from(c);
}

// Laser cannon barrel — thin horizontal pill with white-hot core + cyan halo.
// Mounted on player wings when a laser-family weapon is active.
function bakeLaserCannon(): Texture {
  const W = 44, H = 14;
  const { c, ctx } = makeCanvas(W, H);
  // Outer cyan halo
  const halo = ctx.createRadialGradient(W / 2, H / 2, 1, W / 2, H / 2, H);
  halo.addColorStop(0, 'rgba(0,255,255,0.8)');
  halo.addColorStop(1, 'rgba(0,255,255,0)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, W, H);
  // Barrel body
  ctx.beginPath();
  ctx.roundRect(2, H / 2 - 3, W - 4, 6, 3);
  ctx.fillStyle = '#1a3344';
  ctx.fill();
  ctx.strokeStyle = '#00ffcc';
  ctx.lineWidth = 1.4;
  ctx.stroke();
  // Inner core glow (left → right gradient)
  const core = ctx.createLinearGradient(2, 0, W - 2, 0);
  core.addColorStop(0, 'rgba(255,255,255,0.0)');
  core.addColorStop(0.7, 'rgba(255,255,255,0.85)');
  core.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = core;
  ctx.beginPath();
  ctx.roundRect(4, H / 2 - 1.5, W - 8, 3, 1.5);
  ctx.fill();
  // Tip ring (front of barrel)
  ctx.beginPath();
  ctx.arc(W - 5, H / 2, 4, 0, Math.PI * 2);
  ctx.fillStyle = '#88ffff';
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.2;
  ctx.stroke();
  return Texture.from(c);
}

export const SPRITES = {
  playerP1: bakePlayerShip(0x00ddff, 0xaaffff, 0xff8800, 0xeeffff, 0x44ccff, 0),
  playerP1Tiers: bakePlayerShipTiers(0x00ddff, 0xaaffff, 0xff8800, 0xeeffff, 0x44ccff),
  playerP2: bakePlayer2(),
  particleGlow: bakeParticleGlow(),
  cockpitGlow: bakeRadialGlow(28, 0.22),
  engineGlow: bakeRadialGlow(56, 0.16),
  laserCannon: bakeLaserCannon(),
  enemy: {
    drone:       bakeDrone(),
    scout:       bakeScout(),
    fighter:     bakeFighter(),
    gunship:     bakeGunship(),
    bomber:      bakeBomber(),
    tank:        bakeTank(),
    carrier:     bakeCarrier(),
    stealth:     bakeStealth(),
    interceptor: bakeInterceptor(),
    asteroid:    bakeFallbackDisc(0x888888, 26), // unused — asteroids are runtime Graphics
    miniboss:    bakeFallbackDisc(0xff4400, 50), // unused — miniboss is runtime Graphics
    boss:        bakeFallbackDisc(0xff00ff, 72), // unused — boss is runtime Graphics
  },
};
